import React from 'react';
import { Select } from 'antd';
import { CaretDownOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ErrorMessage from './ErrorMessage';
import { useHistory } from 'react-router-dom';
import { normalizePortalText } from 'utils/baochauTheme';
import {
  findSelectOption,
  getSelectValidation,
  normalizeSelectOptions,
  toLabeledSelectValueList,
  toPlainSelectValue,
  toStoredMultipleSelectValue,
} from './selectHelpers';
import useFormControl from './useFormControl';
import useDeepMemo from 'hooks/useDeepMemo';
import { getFormPopupContainer } from './popupHelpers';
import './style.scss';

const SelectStyle = styled(Select)`
  display: flex;

  &&.bw_form_select_control {
    width: 100%;
    height: auto !important;
    min-height: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .ant-select-selector {
    min-height: 44px !important;
    height: 44px !important;
    padding: 0 14px !important;
    margin: 0 !important;
    border: 1px solid #d6dee9 !important;
    border-radius: 10px !important;
    background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%) !important;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
    align-items: center;
  }

  .ant-select-selector {
    font-size: 14px !important;
    width: 100%;
  }

  .ant-select-arrow .anticon:not(.ant-select-suffix) {
    pointer-events: none;
  }

  .ant-select-arrow {
    right: 14px;
    color: #667085;
  }

  .ant-select-clear {
    right: 14px;
    width: 18px;
    height: 18px;
    margin-top: -9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: transparent;
    color: #667085;
    font-size: 11px;
    box-shadow: none;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .ant-select-clear:hover {
    background: #fff1f0;
    color: #ed3237;
  }

  .ant-select-clear .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
  }

  .bw_select_clear_icon,
  .bw_select_tag_remove_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ant-select-selection-search {
    display: flex;
    align-items: center;
    inset-inline-start: 14px !important;
    inset-inline-end: 34px !important;
  }

  .ant-select-selection-search-input.ant-select-selection-search-input {
    width: 100% !important;
    min-height: 0 !important;
    height: 42px !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    line-height: 42px !important;
  }

  .ant-select-selection-item {
    padding: 0 !important;
    line-height: 42px !important;
    color: #101828;
  }

  .ant-select-selection-placeholder {
    color: #98a2b3 !important;
    line-height: 42px !important;
  }

  &.is-invalid .ant-select-selector {
    border-color: #fda29b !important;
    box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.1), 0 8px 18px rgba(240, 68, 56, 0.06) !important;
  }

  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #f2aaa5 !important;
    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1), 0 8px 18px rgba(15, 23, 42, 0.06) !important;
  }

  td & .ant-select-selector,
  .bw_table & .ant-select-selector {
    min-height: 36px !important;
    height: 36px !important;
    padding: 0 10px !important;
    border-radius: 8px !important;
  }

  td & .ant-select-selection-item,
  td & .ant-select-selection-placeholder,
  .bw_table & .ant-select-selection-item,
  .bw_table & .ant-select-selection-placeholder {
    line-height: 34px !important;
    font-size: 13px !important;
  }

  td & .ant-select-selection-search-input,
  .bw_table & .ant-select-selection-search-input {
    height: 34px !important;
    min-height: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  td & .ant-select-clear,
  .bw_table & .ant-select-clear {
    right: 10px;
    width: 16px;
    height: 16px;
    margin-top: -8px;
    font-size: 10px;
  }

  &.ant-select-multiple .ant-select-selection-item {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    height: 26px !important;
    min-height: 26px !important;
    margin: 2px 4px 2px 0 !important;
    padding: 0 4px 0 8px !important;
    border: 1px solid #ffd7d5 !important;
    border-radius: 999px !important;
    background: #fff5f5 !important;
    color: #ed3237 !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    line-height: 24px !important;
  }

  &.ant-select-multiple .ant-select-selection-item-content {
    overflow: hidden;
    max-width: 180px;
    line-height: 24px !important;
    text-overflow: ellipsis;
  }

  &.ant-select-multiple .ant-select-selection-item-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-left: 4px;
    padding: 0 !important;
    border: 0;
    border-radius: 50%;
    color: #98a2b3 !important;
    line-height: 1;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  &.ant-select-multiple .ant-select-selection-item-remove:hover {
    background: #fee2e2;
    color: #d92d32 !important;
  }

  &.ant-select-multiple .ant-select-selection-item-remove .anticon {
    font-size: 10px;
  }

  body .bw_contain .bw_main .bw_frm_box &&.ant-select-multiple .ant-select-selector.ant-select-selector {
    height: auto !important;
    min-height: 34px !important;
    padding: 4px 34px 4px 10px !important;
    align-items: center !important;
  }

  body .bw_contain .bw_main .bw_frm_box &&.ant-select-multiple .ant-select-selection-overflow {
    min-height: 24px !important;
    row-gap: 4px !important;
    align-items: center !important;
  }

  body .bw_contain .bw_main .bw_frm_box &&.ant-select-multiple .ant-select-selection-search,
  body .bw_contain .bw_main .bw_frm_box &&.ant-select-multiple .ant-select-selection-placeholder {
    height: 24px !important;
    min-height: 24px !important;
    line-height: 24px !important;
  }

  body
    .bw_contain
    .bw_main
    .bw_frm_box
    &&.ant-select-multiple
    .ant-select-selection-search-input.ant-select-selection-search-input {
    height: 24px !important;
    min-height: 24px !important;
    line-height: 24px !important;
  }

  body .bw_contain .bw_main .bw_frm_box &&.ant-select-multiple .ant-select-selection-item {
    height: 24px !important;
    min-height: 24px !important;
    margin: 0 4px 0 0 !important;
    padding: 0 8px !important;
    font-size: 12.5px !important;
    line-height: 24px !important;
  }

  body .bw_contain .bw_main .bw_frm_box &&.ant-select-multiple .ant-select-selection-item-content,
  body .bw_contain .bw_main .bw_frm_box &&.ant-select-multiple .ant-select-selection-item-remove {
    height: 24px !important;
    line-height: 24px !important;
  }

  &.ant-select-multiple .ant-select-selector {
    height: auto !important;
    min-height: 44px !important;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }

  td &.ant-select-multiple .ant-select-selector,
  .bw_table &.ant-select-multiple .ant-select-selector {
    min-height: 36px !important;
  }
`;

const FormSelect = ({
  list = [],
  loading,
  bordered = false,
  placeholder = '--Chọn--',
  className,
  disabled = false,
  field,
  validation,
  mode = null,
  allowClear = true,
  showSearch = true,
  defaultValue,
  showUnresolvedValue = true,
  clearIcon = <CloseOutlined className='bw_select_clear_icon' />,
  removeIcon = <CloseOutlined className='bw_select_tag_remove_icon' />,
  labelInValue = false,
  onChange,
  readOnly = false,
  getPopupContainer = getFormPopupContainer,
  popupClassName,
  dropdownClassName,
  dropdownStyle,
  classNames,
  styles,
  style,
  id,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const stableValidation = useDeepMemo(() => validation, [validation]);
  const selectValidation = React.useMemo(
    () => getSelectValidation(stableValidation, isMultiple),
    [stableValidation, isMultiple],
  );
  const {
    describedBy,
    error,
    errorId,
    errorMessage,
    fieldValue,
    hasError,
    inputId,
    isFlashing,
    setFieldValue,
  } = useFormControl({ field, validation: selectValidation, id, ariaDescribedBy });
  const history = useHistory();
  const selectOptions = React.useMemo(() => normalizeSelectOptions(list), [list]);
  const allowClearConfig = allowClear === true ? { clearIcon } : allowClear;
  const popupRootClassName = [classNames?.popup?.root, popupClassName, dropdownClassName].filter(Boolean).join(' ');
  const popupRootStyle = { ...dropdownStyle, ...styles?.popup?.root };
  const selectedValue = React.useMemo(
    () => {
      if (isMultiple && labelInValue) {
        return toLabeledSelectValueList(fieldValue, selectOptions);
      }

      if (!isMultiple && !labelInValue) {
        const matchedOption = findSelectOption(selectOptions, fieldValue);
        if (matchedOption) return matchedOption.value;
        return showUnresolvedValue ? toPlainSelectValue(fieldValue, false) : undefined;
      }

      return toPlainSelectValue(fieldValue, isMultiple);
    },
    [fieldValue, isMultiple, labelInValue, selectOptions, showUnresolvedValue],
  );
  React.useEffect(() => {
    const elements = document.querySelectorAll('.reload_remove_icon .ant-select-selection-item .ant-select-selection-item-remove');
    const handleClick = () => {
      history.replace('/request-purchase-order/add', {
        pr_list: null,
      });
      window.location.reload();
    };

    elements.forEach(element => {
      element.addEventListener('click', handleClick);
    });

    return () => {
      elements.forEach(element => {
        element.removeEventListener('click', handleClick);
      });
    };
  }, [history]);

  return (
    <React.Fragment>
      <SelectStyle
        id={inputId}
        style={{
          width: '100%',
          ...style,
        }}
        className={['bw_form_select_control', className, hasError ? 'is-invalid' : '', readOnly ? 'is-readonly' : '', isFlashing ? 'bw_field_error_flash' : '']
          .filter(Boolean)
          .join(' ')}
        data-field={field}
        mode={mode}
        suffixIcon={loading ? <LoadingOutlined /> : <CaretDownOutlined />}
        showSearch={showSearch}
        allowClear={allowClearConfig}
        placeholder={typeof placeholder === 'string' ? normalizePortalText(placeholder) : placeholder}
        optionFilterProp='label'
        disabled={disabled || readOnly}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        defaultValue={defaultValue}
        labelInValue={labelInValue}
        value={selectedValue}
        options={selectOptions}
        listHeight={220}
        removeIcon={removeIcon}
        getPopupContainer={getPopupContainer}
        classNames={{
          ...classNames,
          popup: { ...classNames?.popup, root: popupRootClassName || undefined },
        }}
        styles={{
          ...styles,
          popup: { ...styles?.popup, root: popupRootStyle },
        }}
        onChange={(value, option) => {
          if (Array.isArray(value)) {
            setFieldValue(toStoredMultipleSelectValue(value, selectOptions));
          } else {
            setFieldValue(value);
          }
          onChange?.(value, option);
        }}
        {...props}
      />
      {error && <ErrorMessage id={errorId} message={errorMessage} />}
    </React.Fragment>
  );
};

FormSelect.propTypes = {
  field: PropTypes.string,
  className: PropTypes.string,
  validation: PropTypes.object,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  showUnresolvedValue: PropTypes.bool,
  mode: PropTypes.string,
  allowClear: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  clearIcon: PropTypes.node,
  removeIcon: PropTypes.node,
  labelInValue: PropTypes.bool,
  readOnly: PropTypes.bool,
  getPopupContainer: PropTypes.func,
  style: PropTypes.object,
  id: PropTypes.string,
  onChange: PropTypes.func,
};

FormSelect.defaultValue = {
  bordered: false,
  loading: false,
};

export default FormSelect;
